/**
 * SPEC-505 — "the sidebar follows the active page": the DOM-free decision logic.
 *
 * On a navigation (route or ?tab= change) the active page's row AND its sub-tab strip must
 * be visible in the sidebar's scroll container. If they are not, the page's GROUP HEADER is
 * scrolled to the top (`block: 'start'`; the browser clamps at the end of the list). If they
 * already are fully visible, nothing moves. Never driven by the user's own scroll.
 *
 * Kept free of React and of real DOM types so `npm test` (plain Node) can drive it with
 * fakes — jsdom has no layout anyway. `hooks/useFollowActiveNav.ts` is the thin React shell.
 */

export interface RectLike { top: number; bottom: number; height: number }

export interface ElLike {
  parentElement: ElLike | null;
  getAttribute(name: string): string | null;
  querySelector(selector: string): ElLike | null;
  querySelectorAll(selector: string): ArrayLike<ElLike>;
  getBoundingClientRect(): RectLike;
  scrollTop?: number;
  scrollTo?: (arg: { top: number; behavior: 'smooth' | 'auto' }) => void;
}

/** Tracks which navigation has been followed. A key reached while the sidebar is
 *  collapsed stays pending, so re-opening applies the rule exactly once. A key is only
 *  marked done by `done()` (called when the scroll decision actually ran), so an effect
 *  cleanup that cancels the frame — StrictMode, a fast collapse — does not consume it. */
export function createFollowTracker() {
  let handled: string | null = null;
  return {
    pending(navKey: string, collapsed: boolean): boolean {
      return !collapsed && handled !== navKey;
    },
    done(navKey: string): void {
      handled = navKey;
    },
  };
}

function findGroupHeader(container: ElLike, label: string): ElLike | null {
  const headers = container.querySelectorAll('[data-nav-group]');
  for (let i = 0; i < headers.length; i += 1) {
    if (headers[i].getAttribute('data-nav-group') === label) return headers[i];
  }
  return null;
}

/** The aria-current row inside the page's own group (or, for a standalone page, the first
 *  aria-current row in the list). Never the Favourites copy of the row. */
function findActiveRow(container: ElLike, group: string | null): ElLike | null {
  const scope = group ? findGroupHeader(container, group)?.parentElement : container;
  return scope?.querySelector('[aria-current="page"]') ?? null;
}

/** The sticky search box's height, read at scroll time (never a fixed number). */
function stickyHeight(container: ElLike): number {
  const sticky = container.querySelector('[data-tour="nav-search"]');
  return sticky ? sticky.getBoundingClientRect().height : 0;
}

/** Breathing room kept between the sticky box and the header that lands under it. */
const GAP = 8;

/** Row plus the strip beneath it (the row's per-page wrapper) against the scroll
 *  container, minus the sticky search box that covers the container's top edge. */
function fullyVisible(container: ElLike, row: ElLike): boolean {
  const block = row.parentElement?.parentElement ?? row;
  const box = block.getBoundingClientRect();
  const view = container.getBoundingClientRect();
  const inset = stickyHeight(container);
  const top = view.top + inset;
  // A block taller than the room can never fit: asking for it would re-scroll on every
  // ?tab= click. Then the row itself being on screen is enough.
  if (box.height > view.bottom - top) {
    const r = row.getBoundingClientRect();
    return r.top >= top && r.bottom <= view.bottom;
  }
  return box.top >= top && box.bottom <= view.bottom;
}

export interface FollowArgs {
  container: ElLike;
  /** The page's own verb group label, or null for a standalone page. */
  group: string | null;
  /** The group was collapsed and has just been opened: skip the visibility check, its rows
   *  are still animating open. */
  justExpanded: boolean;
  reducedMotion: boolean;
}

/** Scrolls if (and only if) needed. 'missing' = no group header / row in the DOM (search
 *  mode, unmounted): the navigation is NOT consumed, so a later pass can still follow. */
export function followActive({ container, group, justExpanded, reducedMotion }: FollowArgs): 'scrolled' | 'visible' | 'missing' {
  const row = findActiveRow(container, group);
  const target = group ? findGroupHeader(container, group) : row;
  if (!row || !target || typeof container.scrollTo !== 'function') return 'missing';
  if (!justExpanded && fullyVisible(container, row)) return 'visible';
  // Header top, minus the MEASURED sticky box and a gap, so it lands visible, not under it.
  const delta = target.getBoundingClientRect().top - container.getBoundingClientRect().top;
  const top = Math.max(0, (container.scrollTop ?? 0) + delta - stickyHeight(container) - GAP);
  container.scrollTo({ top, behavior: reducedMotion ? 'auto' : 'smooth' });
  return 'scrolled';
}

/** The group-state map with `label` opened. Returns the SAME object when it is already
 *  open (anything but an explicit `false`), so a no-op does not re-render or persist. */
export function withGroupOpen(prev: Record<string, boolean>, label: string): Record<string, boolean> {
  return prev[label] !== false ? prev : { ...prev, [label]: true };
}
